<template>
  <div @click="selectedInputItem = null; selectedTankItem = null">
    <breadcrumbs home="Wine fermentation">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <production-wine-fermenting-preferences @update="preferences=$event"/>
        </v-menu>
      </template>
    </breadcrumbs>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-overlay absolute :opacity="0.15" style="height:calc(100vh - 158px)" v-if="!sites.length">
            <div style="position:relative;top:-100px;width:500px" class="d-inline-block yellow black--text text-body-1 pa-5">
              A wine fermentation cannot be done.<br/>
              There are no sites found that are Excise Wine licensed.
            </div>
          </v-overlay>

          <v-overlay absolute :opacity="0.15" style="height:calc(100vh - 158px)" v-if="showWarning">
            <div style="position:relative;top:-100px;width:400px" class="d-inline-block purple white--text text-body-1 pa-5">
              Before you ferment, you must have {{tanks.length===0?'tanks':'items'}} defined.
              <div class="mt-10 float-right">
                <v-btn :to="{name:'EquipmentTanks'}" color="primary" small v-if="tanks.length===0">Tanks</v-btn>
                <v-btn :to="{name:'InventoryItems'}" color="primary" small v-else-if="items.length===0">Items</v-btn>
              </div>
            </div>
          </v-overlay>

          <v-row dense>
            <v-col cols="6" sm="4" md="3">
              <date-picker v-model="wineFermentation.date" @change="refresh();forDateAndSite();refreshLot()"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2" v-if="preferences.duration">
              <duration-picker label="Duration" v-model="wineFermentation.duration" dense/>
            </v-col>
            <v-col cols="4" sm="2" v-if="preferences.incomplete">
              <v-checkbox label="Completed" v-model="wineFermentation.completed" dense class="mt-1" @change="validate()"/>
            </v-col>
            <v-col cols="4" sm="3" lg="2" :offset-md="3+(preferences.duration?0:2)+(preferences.incomplete?0:2)">
              <v-text-field label="#" v-model="wineFermentation.number" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined dense :counter="20"/>
            </v-col>
          </v-row>
          <v-row dense v-if="sites.length>1" >
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete v-model="wineFermentation.siteId" outlined dense label="Site" :items="sites" item-text="name" item-value="id" @change="forDateAndSite"/>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete v-model="wineFermentation.wineId" outlined dense label="Produced wine" :items="wines" item-text="name" item-value="id"
                                :rules="[fieldRules.required]"  no-data-text="No wine found"/>
            </v-col>
            <v-col v-if="preferences.lot" cols="4" sm="3" lg="2">
              <v-text-field v-model="wineFermentation.lot" label="Wine lot" outlined dense :rules="[fieldRules.required,fieldRules.maxLength(20)]" :counter="20"/>
            </v-col>
          </v-row>

          <v-row dense>
<!--
            <v-col cols="4" sm="3" lg="2">
              <decimal-number-input label="Volume produced" v-model="wineFermentation.volume" prefix="" suffix="litres" :precision="0" integer
                                    :disabled="!edit"/>
            </v-col>
            -->
            <template v-if="preferences.ph">
              <v-col cols="4" sm="3" lg="2" v-if="preferences.brix">
                <decimal-number-input label="Starting Brix" v-model="wineFermentation.osg" prefix="" suffix="°Bx" :precision="1" :max="100"/>
              </v-col>
              <v-col cols="4" sm="3" lg="2" v-else>
                <decimal-number-input label="Starting specific gravity" v-model="wineFermentation.osg" prefix="" suffix="" :precision="3" :min="1" :max="1.9"/>
              </v-col>
              <v-col cols="4" sm="3" lg="2">
                <decimal-number-input label="pH" v-model="wineFermentation.ph" prefix="" suffix="" :precision="1" :max="14"/>
              </v-col>
            </template>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <outlined-block v-model="wineFermentation.items" :error-messages="itemsError" ref="fermentationItems" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="wineFermentation.items" no-data-text="At least one input item is required"
                              :headers="[{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                                          {text:'Fermentation input',value:'input',width:'45%',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                                          {text:'Lot',value:'lot',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt-table-top'},
                                          {text:'Quantity',value:'quantity',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1',align:'right'}]"
                              class="no-sort"
                              :item-class="item => item.id=== selectedInputItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="item => {selectedInputItem = item.id; $nextTick(() => this.$refs.form.validate())}"
                              ref="itemTableBody">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n2" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.input="{item}">
                    <template v-if="selectedInputItem === item.id">
                      <multi-level-select v-if="categories.length" :menuData="eligibleItems" :second.sync="item.itemId" @change="changeItem(item)" outlined label="Input"/>
                      <v-autocomplete v-else :items="items" item-text="name" item-value="id" v-model="item.itemId" dense @change="changeItem(item)" :rules="[fieldRules.required]" class="text-body-2" autofocus outlined label="Input"/>
                    </template>
                    <div v-else class="ml-1" :class="{'error--text':!item.itemId}">{{fermentationItem(item).name}}</div>
                  </template>
                  <template v-slot:item.lot="{item}">
                    <template v-if="fermentationItem(item).lotTracking">
                      <v-combobox v-if="selectedInputItem === item.id" :items="currentLots(item)" v-model="item.lot" :rules="[lotValid,fieldRules.maxLength(20)]" dense class="text-body-2"
                                  outlined label="Lot" @change="changeItemLot(item);validate()"/>
                      <div v-else class="ml-1" :class="{'error--text':lotValid(item.lot)!==true}">{{lotValid(item.lot)!==true ? 'MISSING' : item.lot}}</div>
                    </template>
                    <div v-else class="ml-1">n/a</div>
                  </template>
                  <template v-slot:item.quantity="{item}">
                    <decimal-number-input v-if="selectedInputItem === item.id" label="Quantity" v-model="item.quantity" prefix=""
                          :suffix="fermentationItem(item).trackUnits" :precision="fermentationItem(item).precision" class="text-body-2"
                          :hint="fermentationId?'':fermentationItem(item).quantity" persistent-hint outlined/>
                    <div v-else class="mr-1" :class="{'error--text':item.quantity < 0}">
                      {{item.quantity | number(fermentationItem(item).precision)}} {{fermentationItem(item).trackUnits}}
                    </div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedInputItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab :disabled="!edit || items.length===0" color="primary" x-small @click.stop="addItem">
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <outlined-block v-model="wineFermentation.tanks" :rules="[tanksNeeded]" :error-messages="tanksError" ref="tankItems" :disabled="!edit">
                <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                              :items="wineFermentation.tanks" no-data-text="At least one tank is required"
                              :headers="[{text:'',value:'add',width:'10%',align:'center',cellClass:'pa-0',divider:true},
                                          {text:'Tank',value:'output',width:'55%',divider:true,class:'px-2',cellClass:'py-2 px-1'},
                                          {text:'Volume',value:'volume',width:'30%',divider:true,class:'px-2',cellClass:'py-2 px-1',align:'right'}]"
                              class="no-sort"
                              :item-class="tank => tank.tankId=== selectedTankItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                              @click.native.stop=""
                              @click:row="tank => {selectedTankItem = tank.tankId; $nextTick(() => this.$refs.form.validate())}"
                              ref="tankTableBody">
                  <template v-slot:item.add="{index}">
                    <v-icon class="handle mt-n2" :disabled="!edit">mdi-drag</v-icon>
                    <v-btn class="mt-n2" icon @click.stop="deleteTank(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                  </template>
                  <template v-slot:item.output="{item}">
                    <tank-select v-if="selectedTankItem === item.tankId" v-model="item.tankId" :tanks="validOutputTanks(item.tankId)" :required="wineFermentation.completed"
                                 :highlight="tank => fermentationId === undefined && (tank.contents.alcoholId === item.spiritId || tank.contents.alcoholId === null)"
                                 :hint="fermentationId===undefined"
                                 outlined label="Tank"/>
<!--
                    <v-autocomplete v-if="selectedTankItem === item.tankId"
                                    dense label="Tank" v-model="item.tankId" :items="eligibleTanks(item.tankId)" item-value="id" item-text="name" class="text-body-2"
                                    :hint="tankVolume(item.tankId)" persistent-hint :rules="[fieldRules.required]" outlined/>
-->
                    <div v-else class="ml-1">{{getTank(item.tankId).name}}</div>
                  </template>
                  <template v-slot:item.volume="{item}">
                    <decimal-number-input v-if="selectedTankItem === item.tankId" label="Volume" v-model="item.volume" prefix=""
                          suffix="litres" :precision="0" integer class="text-body-2"/>
                    <div v-else class="mr-1" :class="{'error--text':item.volume < 0}">{{item.volume | number(0)}} litres</div>
                  </template>
                  <template v-slot:footer="{widths}">
                    <v-divider/>
                    <div @click="selectedTankItem = null" style="width:100%" class="py-2 px-0">
                      <div :style="'width:'+widths[0]+'px'" class="text-center">
                        <v-btn fab :disabled="!edit || eligibleTanks().length===0" color="primary" x-small @click.stop="addTank">
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </template>
                </v-data-table>
              </outlined-block>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" lg="8">
              <v-textarea label="Comments" rows="4"
                           v-model="wineFermentation.comments" outlined dense :rules="[fieldRules.maxLength(500)]" counter="500"/>
            </v-col>
          </v-row>

          <v-row v-if="fermentationId">
            <v-col>
              <v-btn outlined small @click="showNotes=true">View notes</v-btn>
            </v-col>
          </v-row>

        </v-container>
      </v-form>

      <v-divider/>
      <v-card-actions class="px-6">
        <v-switch v-if="fermentationId && company.permissions.includes('production.write')" :disabled="dirty"
                  v-model="edit" label="Edit" hide-details class="mt-0" @change="forDateAndSite()"/>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
    <production-wine-fermenting-notes :fermentationId="fermentationId" v-model="showNotes" :allow-add="false"/>
  </div>
</template>

<style>
  .pt-table-top {vertical-align: top;padding-bottom:10px !important}
</style>

<script>
import {format} from 'date-fns';
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import ProductionWineFermentingNotes from '@/views/production/wineFermenting/ProductionWineFermentingNotes'
import ProductionWineFermentingPreferences from '@/views/production/wineFermenting/ProductionWineFermentingPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'
import TankSelect from '@/components/TankSelect'

export default {
  name: 'ProductionWineFermentingEdit',
  mixins: [confirmLeave,createHierarchyList],
  components: {TankSelect,ProductionWineFermentingNotes,ProductionWineFermentingPreferences},
  props: {
    fermentationId: {type:String, required:false},
//    volume: {type:Number, required:false, default:1000},
    activityId: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      wineFermentation: {date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),number:'',completed:true,duration:0,osg:1.101,fsg:1,ph:3.5,lot:'',
             comments:'',items:[],tanks:[],siteId:this.$store.getters.sites(true, {B266:true})[0].id,wineId:null,activityId:this.activityId},
      editingIncomplete: false,
      brix: false,
      preferences: {},
      items: [],
      categories: [],
      wines: [],
      noWine: false,
      itemsError: '',
      tanks: [],
      tanksError: '',
      dirty: false,
      saving: false,
      loading: false,
      selectedInputItem: null,
      selectedTankItem: null,
      valid: true,
      lotValid: lot => !this.wineFermentation.completed || !!lot || 'Lot is mandatory',
      showNotes: false,
      sortableItem: null,
      sortableTank: null,
      showWarning: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.fermentationId, {B265:true});
    },
    wine() {
      return this.wines.find(w => w.id === this.wineFermentation.wineId);
    },
    eligibleItems() {
      const categories = this.createHierarchyList(this.categories);
      for (const category of categories) {
        const sub = this.items.filter(item => item.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      const items = [...categories.filter(category => !category.disabled)];
      const items = categories;
      const leftOver = this.items.filter(item => !item.category?.id);
      if (leftOver.length)
        items.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      return items;
    }
  },
  watch: {
    wineFermentation: {
      handler() {
        this.dirty = true;
//        this.checkTanks();
      },
      immediate: true,
      deep: true
    },
    'preferences.brix'() {
      if (!this.brix && this.preferences.brix)
        this.wineFermentation.osg = this.$roundNumber((((182.4601 * this.wineFermentation.osg -775.6821) * this.wineFermentation.osg +1262.7794) * this.wineFermentation.osg -669.5622), 1);
      else if (this.brix && !this.preferences.brix)
        this.wineFermentation.osg = this.$roundNumber((this.wineFermentation.osg / (258.6-((this.wineFermentation.osg / 258.2)*227.1))) + 1, 3);
      this.brix = this.preferences.brix;
    }
  },
  async mounted() {
    this.loading = true;
    await Promise.all([
      this.$lazyListLoad(this.wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(this.items, 'items2', 'id,name,sku,trackUnits,allowNegativeInventory,type,lotTracking,precision,category{id}', 100, !this.fermentationId ? [{field:'active', comparator:'eq', value:'true'}] : [])
    ]);
    const results = await this.$apiCall(`query ProductionWineFermentingEdit($edit:Boolean!, $id:ID!) {
                                              wineFermentation(id:$id) @include(if:$edit) {
                                                id,date,number,created,site{id},completed,duration,wine{id},osg,fsg,ph,lot,comments,
                                                items{itemId,quantity,lot},
                                                tanks{tankId,volume}
                                              }
                                              wineFermentationPreferences {
                                                brix,duration,incomplete,ph,lot
                                              }
                                            }`,
                                        {edit:this.fermentationId!=undefined,id:this.fermentationId || ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.preferences = results.data.wineFermentationPreferences;
    this.brix = this.preferences.brix;
    if (this.fermentationId) {
      let id = Date.now();
      await this.$lazyListLoad(this.tanks, 'tanks2', `id,name,capacity,site{id}`, 100);
      results.data.wineFermentation.items.forEach(item => item.id = id++);
      this.wineFermentation = results.data.wineFermentation;
      this.wineFermentation.siteId = this.wineFermentation.site.id;
      delete this.wineFermentation.site;
      this.wineFermentation.wineId = this.wineFermentation.wine?.id ?? null;
      delete this.wineFermentation.wine;
      this.editingIncomplete = !this.wineFermentation.completed;
      if (!this.wineFermentation.completed)
        this.wineFermentation.duration = this.$roundNumber(Date.now()/60_000 - this.wineFermentation.created, 0);
    }
    else
      await this.refresh();
    if (this.preferences.brix)
      this.wineFermentation.osg = this.$roundNumber((((182.4601 * this.wineFermentation.osg -775.6821) * this.wineFermentation.osg +1262.7794) * this.wineFermentation.osg -669.5622), 1);

    this.noWine = !this.wines.length && !this.$vuetify.breakpoint.mobile && this.tanks.length && this.items.length;

    if (!this.fermentationId) {
      const results = await this.$apiCall(`query ProductionWineFermentingEdit {
                                              wineFermentationNextNumber
                                            }`);
      if (!results.error)
        this.wineFermentation.number = results.data.wineFermentationNextNumber;
      this.edit = true;
      this.validate();
    }
    else {
      if (!this.wineFermentation.completed)
        this.edit = true;
    }

    await this.refreshLot();
    this.$nextTick(() => this.dirty = false);

    this.loading = false;

    setTimeout(() => this.showWarning = this.tanks.length===0 || this.items.length===0, 2_000);

    const _self = this;

    this.sortableItem = sortableJS.create(this.$refs.itemTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        if (newIndex === _self.wineFermentation.items.length) {
          console.log('nope')
          return;
        }
        const rowSelected = _self.wineFermentation.items.splice(oldIndex, 1)[0];
        _self.wineFermentation.items.splice(newIndex, 0, rowSelected);
      }
    })

    this.sortableTank = sortableJS.create(this.$refs.tankTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.wineFermentation.tanks.splice(oldIndex, 1)[0];
        _self.wineFermentation.tanks.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortableItem.destroy();
    this.sortableTank.destroy();
  },

  methods: {
    getTank(tankId) {
      return this.tanks.find(tank => tank.id === tankId) ?? {name : 'MISSING', contents:{volume:0}};
    },
    validOutputTanks(includeId) {
      return this.tanks.filter(tank =>
        tank.id === includeId || !this.wineFermentation.tanks.some(o => o.tankId === tank.id)
      );
    },
    async refresh() {
      if (this.fermentationId)
        return;
      this.tanks = [];
      this.$lazyListLoad(this.tanks, 'tanks2', `id,name,capacity,site{id},contents(date:"${this.wineFermentation.date}") {volume,alcoholType,litres,kg,strength,alcoholName,alcoholId}`, 100, [{field:'active', comparator:'eq', value:'true'}]);
    },
    validate() {
      this.$nextTick(() => {
        this.$refs.form?.validate();
      });
    },
    tankVolume(tankId) {
      if (this.fermentationId)
        return;
      return `${this.$number(this.getTank(tankId).contents.volume)} litres`;
    },
    fermentationItem(item) {
      return this.items.find(i => i.id === item.itemId) || {name:'MISSING', precision:0, trackUnits:''};
    },
    currentLots(item) {
      const itemObject = this.fermentationItem(item);
      return itemObject.sites ? itemObject.sites.filter(site => site.siteId === this.wineFermentation.siteId).map(site => site.lot) : [];
    },
    changeItemLot(item) {
      const itemObject = this.fermentationItem(item);
      const site = itemObject.sites && itemObject.sites.find(site => site.siteId === this.wineFermentation.siteId && site.lot === item.lot);
      itemObject.quantity = `${site ? this.$number(site.quantity, itemObject.precision) : '0'} ${itemObject.trackUnits}`;
    },
    /*
    checkTanks() {
      if (!this.wineFermentation.completed)
        return true;
      // Make sure any tank specified exists,
      // its volume doesn't exceed its capacity, and the total volume matches the fermentation volume
      this.tanksError = '';
      let totalVolume = 0;
      for (let i = this.wineFermentation.tanks.length-1; i >= 0; i--) {    // Do it this way since we may have to remove element
        const tank = this.wineFermentation.tanks[i];
        if (tank.tankId === '')   // Unspecified tank line - no need to do checking
          return;
        const tankObject = this.tanks.find(f => f.id === tank.tankId && f.site.id === this.wineFermentation.siteId);
        if (!tankObject)               // Cannot find tank
          this.wineFermentation.tanks.splice(i, 1);
        else
          totalVolume += tank.volume;
      }
      if (this.tanksError === '' && this.tanks.length !== 0 && this.wineFermentation.tanks.length !== 0 && totalVolume < this.wineFermentation.volume)
        this.tanksError = `Total tank volume (${totalVolume} litres) must be at least the fermentation volume (${this.wineFermentation.volume} litres)`;
    },
    */

    tanksNeeded(tanks) {
      if (!this.wineFermentation.completed)    // No checking necessary on an incomplete fermentation
        return true;
      if (tanks.length === 0)
        return 'At least one tank needs to be used for the fermentation';

      return true;
    },

    async changeItem(fermentationItem) {
      const item = this.fermentationItem(fermentationItem);
      if (item.id && !item.sites) {
        const results = await this.$apiCall(`query ProductionWineFermentingEdit($id:ID!, $date:DateTime!) {
                                                  item(id:$id) {
                                                    sites(asof:$date) {siteId,lot,quantity}
                                                  }
                                                }`,
                                            {id:fermentationItem.itemId,date:this.wineFermentation.date});
        if (!results.error)
          this.$set(item, 'sites', results.data.item.sites);
      }
      if (!this.fermentationId)
        fermentationItem.lot = null;
      this.validate();
    },

    async forDateAndSite() {
      this.loading = true;
      for (const item of this.wineFermentation.items) {
        const itemObject = this.fermentationItem(item);

        // Get proper quantity and lot info for current date and site
        delete itemObject.sites;
        await this.changeItem(item);

        // Make sure lot is still valid, unless this is an edit where we'll assume it must be valid
        if (!this.fermentationId && !this.currentLots(item).some(lot => lot === item.lot)) {
          item.lot = '';
          itemObject.quantity = '';
        }
      }
      this.loading = false;

//      this.checkTanks();
      this.validate();
    },
    eligibleTanks(includeTankId) {
      return this.tanks.filter(tank => tank.site.id === this.wineFermentation.siteId && (tank.id === includeTankId || !this.wineFermentation.tanks.some(f => f.tankId === tank.id)));
    },
    async addItem() {
      const fermentationItem = {id:Date.now(),itemId:'',lot:null, quantity:0};
      this.wineFermentation.items.push(fermentationItem);
      await this.changeItem(fermentationItem);
      this.selectedInputItem = fermentationItem.id;
      this.validate();
    },
    deleteItem(index) {
      this.wineFermentation.items.splice(index, 1);
    },
    addTank() {
      const tankId = this.eligibleTanks()[0].id;
      this.wineFermentation.tanks.push({tankId,volume:0,id:Date.now()});
      this.selectedTankItem = tankId;
      this.validate();
    },
    deleteTank(index) {
      this.wineFermentation.tanks.splice(index, 1);
    },
    async refreshLot() {
      if (this.fermentationId)    // Don't do for editing existing fermentation
        return;
      this.loading = true;
      const results = await this.$apiCall(`query ProductionWineFermentingEdit($asof:DateTime!) {
                                            lotUnique(prefix:"W",date:$asof)
                                          }`,
                                          {asof:this.wineFermentation.date});
      if (!results.error)
        this.wineFermentation.lot = results.data.lotUnique;

      this.loading = false;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.wineFermentation.items.some(item => !item.itemId ||
                                                      (this.fermentationItem(item).lotTracking && this.lotValid(item.lot) !== true) ||
                                                      item.quantity < 0) ||
                         this.wineFermentation.tanks.some(tank => tank.volume < 0);
      if (!this.$refs.form.validate() || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }

      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {items, tanks, created, ...input} = this.wineFermentation;
      input.items = this.wineFermentation.items.map(item => {
        // eslint-disable-next-line no-unused-vars
        const {id,open,incomplete,...itemData} = item;
        return itemData;
      });
      input.tanks = this.wineFermentation.tanks.map(tank => {
        // eslint-disable-next-line no-unused-vars
        const {id,open,incomplete,...tankData} = tank;
        return tankData;
      });
      if (this.preferences.brix)
        input.osg = this.$roundNumber((input.osg / (258.6-((input.osg / 258.2)*227.1))) + 1, 3);
      const results = await this.$apiCall(`mutation ProductionWineFermentingEdit($input: WineFermentationInput!) {
                                             wineFermentationCreate(input:$input) {id}
                                           }`,
                                          {input})
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.wineFermentation.id?'Updated':'Created'} wine fermentation`});
        if (this.activityId)
          this.$store.dispatch('checkTasks', true);
      }
    }
  }
}
</script>